import { useEffect, useRef } from "react";
import { useDocumentTitle } from "../a11y";
import { availabilityWord } from "../people";
import { Portrait } from "../portrait/Portrait";
import type { IntroView } from "../types";

export const HOW_THIS_WORKS: string[] = [
  "People come to your door, and others wait outside it.",
  "Every choice costs time, and sometimes political capital or energy.",
  "Your hours this week are always shown.",
  "You can set standing instructions and commission processes so that fewer things need you.",
  "There are no right answers, only consequences, some of which arrive weeks later.",
  "At the end you'll see what happened and why.",
];

/** The scenario introduction: premise, your role, how this works and who's who. */
export function IntroContent({ intro, title, headingLevel = 1 }: { intro: IntroView; title: string; headingLevel?: 1 | 2 }) {
  const H = headingLevel === 1 ? "h1" : "h2";
  const Sub = headingLevel === 1 ? "h2" : "h3";
  return (
    <>
      <H className="intro__title" id="intro-title" tabIndex={-1}>{title}</H>
      <p className="intro__premise">{intro.premise}</p>
      <section className="intro__block" aria-labelledby="intro-role">
        <Sub id="intro-role">Your role</Sub>
        <p>{intro.role}</p>
      </section>
      <section className="intro__block" aria-labelledby="intro-how">
        <Sub id="intro-how">How this works</Sub>
        <ul className="intro__how">
          {HOW_THIS_WORKS.map((line) => <li key={line}>{line}</li>)}
        </ul>
      </section>
      {intro.cast.length > 0 && (
        <section className="intro__block" aria-labelledby="intro-cast">
          <Sub id="intro-cast">Who's who</Sub>
          <p className="intro__cast-note">{castNote(intro)}</p>
          <ul className="intro__cast">
            {intro.cast.map((p) => {
              const away = availabilityWord(p.availability);
              return (
                <li key={p.id} className="cast-card">
                  <Portrait portraitId={p.portraitId} seed={p.id} name={p.name} role={p.role} grade={p.grade} expression="neutral" size={88} />
                  <div>
                    <p className="cast-card__name">{p.name}</p>
                    <p className="cast-card__role">{p.role}</p>
                    {away && <p className="person-card__away">{away}</p>}
                    {p.bio && <p className="cast-card__bio">{p.bio}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
          {intro.firm.teams.length > 0 && (
            <>
              <p className="intro__cast-note">The rest of the firm works in these teams:</p>
              <ul className="intro__teams">
                {intro.firm.teams.map((t) => <li key={t.name}>{teamLine(t)}</li>)}
              </ul>
            </>
          )}
        </section>
      )}
      <p className="intro__accuracy muted">
        Regulatory and accounting content (the SRA, money laundering, client money and the accounts rules) is simplified
        for the game. It is illustrative, not legal or compliance advice, and the rules change.
      </p>
    </>
  );
}

/** Why only some of the firm is introduced (09 §4: everyone else is modelled as teams). */
export function castNote(intro: IntroView): string {
  const { headcount, others } = intro.firm;
  const named = intro.cast.length;
  if (others <= 0) return `These are the ${named} people you work with.`;
  return `${intro.firm.headcount > 0 ? `The firm has ${headcount} people. ` : ""}These ${named} are the ones you'll deal with directly: ` +
    `the people whose problems, decisions and conversations come to you. The other ${others} work in their teams. You won't meet them one ` +
    `by one, but you'll feel them: their workload, their mood and their mistakes reach you through their team leads.`;
}

export function teamLine(t: IntroView["firm"]["teams"][number]): string {
  const parts = [t.lawyers ? `${t.lawyers} lawyer${t.lawyers === 1 ? "" : "s"}` : "", t.support ? `${t.support} support staff` : ""].filter(Boolean);
  return `${t.name}: ${parts.join(", ")}`;
}

/** Shown once, before the office, when a run starts. */
export function IntroScreen({ intro, title, firmName, onBegin }: { intro: IntroView; title: string; firmName: string; onBegin: () => void }) {
  const ref = useRef<HTMLElement>(null);
  useDocumentTitle(`Introduction · ${firmName}`);
  useEffect(() => { ref.current?.querySelector<HTMLElement>("#intro-title")?.focus({ preventScroll: true }); }, []);
  return (
    <main ref={ref} className="entry entry--wide intro" aria-labelledby="intro-title">
      <p className="entry__kicker">{firmName}</p>
      <IntroContent intro={intro} title={title} />
      <div className="intro__actions">
        <button type="button" className="btn btn--primary" onClick={onBegin}>Begin</button>
      </div>
    </main>
  );
}

/** The same introduction, reopened from the menu or the nav as "Who's who". */
export function IntroPanel({ intro, title }: { intro: IntroView | null; title: string }) {
  return (
    <section className="panel intro intro--panel" aria-labelledby="intro-title">
      {intro ? <IntroContent intro={intro} title={title} headingLevel={2} /> : (
        <>
          <h2 className="panel-title" id="intro-title">Who's who</h2>
          <p className="panel-note">There is no introduction for this scenario.</p>
        </>
      )}
    </section>
  );
}
